import { FC, useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { logger } from 'shared/services/logger'
import type {
  LogEntry,
  LogExport,
  LoggerHealth,
  SessionSnapshot
} from 'shared/services/logger/logger.types'
import { useDesktopUtilityWindowsStore } from 'shared/stores/desktopUtilityWindowsStore'

import { UtilityWindowWrapper } from '../UtilityWindowWrapper'
import styles from './LogViewer.module.scss'
import { LogDropzone } from './components/LogDropzone'
import { LogList } from './components/LogList'
import { LogModeTabs } from './components/LogModeTabs'
import { LogToolbar } from './components/LogToolbar'
import { useLogFilter } from './hooks/useLogFilter'
import { useStackResolver } from './hooks/useStackResolver'
import type { Mode } from './logViewer.types'

export const LogViewer: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.log_viewer'
  })
  const isOpen = useDesktopUtilityWindowsStore(
    state => state.activeWindow === 'log-viewer'
  )
  const closeWindow = useDesktopUtilityWindowsStore(state => state.closeWindow)

  const [mode, setMode] = useState<Mode>('live')
  const [liveEntries, setLiveEntries] = useState<LogEntry[]>([])
  const [liveSnapshot, setLiveSnapshot] = useState<SessionSnapshot | null>(null)
  const [fileData, setFileData] = useState<LogExport | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [health, setHealth] = useState<LoggerHealth | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const activeEntries =
    mode === 'live' ? liveEntries : (fileData?.entries ?? [])
  const activeSnapshot =
    mode === 'live' ? liveSnapshot : (fileData?.snapshot ?? null)

  const filter = useLogFilter(activeEntries)
  const { resolvedStacks, resolvingIds, resolveEntryStack } = useStackResolver()

  const loadLiveLogs = useCallback(async () => {
    setIsLoading(true)
    try {
      const entries = await logger.readAll()
      setLiveEntries(entries)
      setLiveSnapshot(logger.getSnapshot())
      setHealth(logger.getHealth())
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen && mode === 'live') loadLiveLogs()
  }, [isOpen, mode, loadLiveLogs])

  useEffect(() => {
    if (!isOpen || mode !== 'live') return
    let timer: ReturnType<typeof setTimeout> | undefined
    const unsubscribe = logger.subscribe(() => {
      clearTimeout(timer)
      timer = setTimeout(() => void loadLiveLogs(), 200)
    })
    return () => {
      clearTimeout(timer)
      unsubscribe()
    }
  }, [isOpen, mode, loadLiveLogs])

  const handleSwitchMode = (m: Mode) => {
    setMode(m)
    filter.clearAll()
    setExpandedIds(new Set())
    if (m === 'live') loadLiveLogs()
  }

  const handleClearLogs = async () => {
    await logger.clear()
    setLiveEntries([])
  }

  const toggleExpand = useCallback(
    (entry: LogEntry) => {
      setExpandedIds(prev => {
        const next = new Set(prev)
        if (next.has(entry.id)) {
          next.delete(entry.id)
        } else {
          next.add(entry.id)
          const hasStack =
            typeof entry.error?.stack === 'string' ||
            typeof entry.payload.stack === 'string'
          const alreadyResolved = Array.isArray(entry.payload.resolvedStack)
          if (hasStack && !resolvedStacks.has(entry.id) && !alreadyResolved) {
            resolveEntryStack(entry)
          }
        }
        return next
      })
    },
    [resolvedStacks, resolveEntryStack]
  )

  const showLogView = mode === 'live' || (mode === 'file' && fileData !== null)

  return (
    <UtilityWindowWrapper
      isOpen={isOpen}
      title={t('title')}
      onClose={closeWindow}
      className={styles.wideWindow}
    >
      <div className={styles.container}>
        <LogModeTabs mode={mode} onSwitch={handleSwitchMode} />

        {mode === 'file' && !fileData && <LogDropzone onFile={setFileData} />}

        {showLogView && (
          <>
            <LogToolbar
              {...filter}
              mode={mode}
              fileData={fileData}
              activeEntries={activeEntries}
              onRefresh={loadLiveLogs}
              onExport={() => logger.export()}
              onClearLogs={handleClearLogs}
              onLoadAnother={() => setFileData(null)}
            />
            <LogList
              entries={filter.filteredEntries}
              totalCount={activeEntries.length}
              snapshot={activeSnapshot}
              health={mode === 'live' ? health : (fileData?.health ?? null)}
              isLoading={isLoading}
              isAnyFilterActive={filter.isAnyFilterActive}
              expandedIds={expandedIds}
              onToggleExpand={toggleExpand}
              resolvedStacks={resolvedStacks}
              resolvingIds={resolvingIds}
            />
          </>
        )}
      </div>
    </UtilityWindowWrapper>
  )
}
